'use strict';

import Swiper from 'swiper';
import { Navigation, Mousewheel, FreeMode, Pagination } from 'swiper/modules';

const SCROLLER_SELECTOR = '.js-scroller';

export function initialiseScroller(element) {
    const scrollerElement = element.querySelector('.swiper-container');
    const paginationElement = element.querySelector('.swiper-pagination');
    if (!scrollerElement) return null;

    const mq = window.matchMedia('(max-width: 999px)');
    let scroller;

    const settings = {
        slidesPerView: 'auto',
        simulateTouch: false,
        modules: [
            FreeMode,
            Mousewheel,
            Navigation,
            Pagination
        ],
        freeMode: {
            enabled: true,
        },
        mousewheel: {
            releaseOnEdges: true,
            forceToAxis: true,
            sensitivity: 0.8,
        },
        navigation: {
            enabled: true,
            nextEl: element.querySelector('.scroller-next'),
            prevEl: element.querySelector('.scroller-prev'),
        },
        pagination: {
            el: paginationElement,
            type: "fraction",
            renderFraction(currentClass, totalClass) {
                return `<span class="visually-hidden">Slide </span>`
                     + `<span class="${currentClass}"></span>`
                     + `<span class="swiper-pagination-divider"> / </span>`
                     + `<span class="${totalClass}"></span>`;
            },
        },
        on: {
            init() {
                var activeIndex = this.activeIndex;
                var activeSlide = document.getElementsByClassName('swiper-slide')[activeIndex];
                var activeSlideVideo = activeSlide?.querySelector('.scroller-video');

                if (!pauseButton) {
                    return;
                }

                var pauseButton = document.querySelector('.scroller-pause');

                if (pauseButton) pauseButton.classList.toggle('is-video', Boolean(activeSlideVideo));
            },

            transitionStart: function(){

                var videos = document.querySelectorAll('.scroller-video');

                Array.prototype.forEach.call(videos, function(video){
                    video.pause();
                });
            },

            transitionEnd: function(){
                var activeIndex = this.activeIndex;
                var activeSlide = document.getElementsByClassName('swiper-slide')[activeIndex];
                var activeSlideVideo = activeSlide?.querySelector('.scroller-video');
                var pauseButton = document.querySelector('.scroller-pause');

                if (!pauseButton) {
                    return;
                }

                if (activeSlideVideo) {
                    pauseButton.classList.add('is-video');
                    activeSlideVideo.play();
                } else {
                    pauseButton.classList.remove('is-video');
                    pauseButton.classList.remove('is-paused');
                }
            },
        }
    };

    const initializeSwiper = () => {
        scroller = new Swiper(scrollerElement, settings);
    };

    const destroySwiper = () => {
        if (scroller) {
            scroller.destroy(true, true);
            scroller = null;
        }
    };

    let reapplyVenueFilter;

    const handleMediaQueryChange = event => {
        if (event.matches) {
            destroySwiper();
        } else {
            initializeSwiper();
        }
        if (reapplyVenueFilter) reapplyVenueFilter();
    };

    // Check initial state - only initialize if not matching mobile breakpoint
    if (!mq.matches) {
        initializeSwiper();
    }

    mq.addEventListener('change', handleMediaQueryChange);

    reapplyVenueFilter = initialiseVenueSwitcher(element);

    // Custom Tab handling (keyboard focus only)
    scrollerElement.addEventListener('focusin', event => {
        if (event.target && event.target.matches(':focus-visible')) { // Check if focus is keyboard-driven
            const focusedElement = event.target;
            const slide = focusedElement?.closest('.swiper-slide');

            if (slide && scroller) {
                scroller.slideTo(scroller.slides.indexOf(slide));
            }
        }
    });

    // Add pause functionality to the scroller if there are videos
    const sliderPause = scrollerElement.querySelector('.scroller-pause');

    if (sliderPause) {
        const getActiveVideo = () => {
            const activeSlide = document.querySelector('.swiper-slide.swiper-slide-active');
            return activeSlide ? activeSlide.querySelector('.scroller-video') : null;
        };

        sliderPause.addEventListener('click', () => {
            const activeVideo = getActiveVideo();
            if (activeVideo) {
                if (activeVideo.paused) {
                    activeVideo.play();
                    sliderPause.classList.remove('is-paused');
                } else {
                    activeVideo.pause();
                    sliderPause.classList.add('is-paused');
                }
            }
        });
    }

    function initialiseVenueSwitcher(el) {
        const select = el.querySelector('.event-scroller__venue-select');
        if (!select) return null;

        const slides = Array.from(el.querySelectorAll('.event-scroller__item[data-venue]'));

        const applyFilter = () => {
            const selected = select.value;
            slides.forEach(slide => {
                slide.style.display = (!selected || selected === 'all_venues' || slide.dataset.venue === selected) ? '' : 'none';
            });
            if (scroller) scroller.update();
        };

        // Already initialised — options are populated, just return a fresh filter reference
        if (select.options.length > 1) return applyFilter;

        const venues = [];
        const seen = new Set();
        slides.forEach(slide => {
            const venue = slide.dataset.venue;
            if (venue && !seen.has(venue)) {
                seen.add(venue);
                venues.push(venue);
            }
        });

        if (venues.length <= 1) {
            select.closest('.event-scroller__venue-switcher').style.display = 'none';
            return null;
        }

        venues.forEach(venue => {
            const option = document.createElement('option');
            option.value = venue;
            option.textContent = venue;
            select.appendChild(option);
        });

        select.addEventListener('change', applyFilter);

        return applyFilter;
    }

    return scroller;
}

export default function () {
    const activeScrollers = Array.from(document.querySelectorAll(SCROLLER_SELECTOR)).map(initialiseScroller).filter(Boolean);

    // Fix for Basker Preview
    const observer = new MutationObserver(() => {
        document.querySelectorAll(SCROLLER_SELECTOR).forEach(function (scroller) {
            const container = scroller.querySelector('.swiper-container');
            if (!activeScrollers.some(function (existingScroller) {
                return existingScroller && existingScroller.el === container;
            })) {
                const newScroller = initialiseScroller(scroller);
                if (newScroller) activeScrollers.push(newScroller);
            }
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
}
